import { Cookies, useCookies } from 'react-cookie';
import ReactGA from 'react-ga4';
import { useCallback, useMemo, useState, useEffect } from 'react';
import { Apis } from '../domain/Apis';
import { useGetStationByIdMutation } from '../redux/login/explore/GbfsServiceApi';
import { useSelector } from 'react-redux';
import { agencyInformation } from '../redux/slices/agencySlice';
import { useSearchNoraMutation } from '../redux/login/search/SearchServiceApi';
import GeneralUtils from './GeneralUtils';

interface AnalyticsEvent {
  category?: string;
  action: string;
  label?: string;
  platform_type?: string;
  [key: string]: any;
}

export enum FavouriteTypes {
  OPERATOR = 'Operador',
  LOCATION = 'Ubicación',
  POI = 'Punto de interés',
  ROUTE = 'Ruta',
  STOP = 'Parada',
  BIKE_STATION = 'Estación de bicicletas',
}

export interface sendEventForMenuClick {
  label: string;
  shouldSend: boolean;
}

export interface sendEventForFavorites {
  type: FavouriteTypes;
  label: string;
  shouldSend: boolean;
}

interface MapMarkerResult {
  label?: string | null;
  type?: MapMarkerResultLabel | null;
}

export enum MapMarkerResultLabel {
  DIRECTION = 'Dirección',
  POI = 'Punto de interés',
  STOP = 'Parada',
  STOP_GROUP = 'Grupo de paradas',
  POI_GROUP = 'Grupo de puntos de interés',
  MARKERS_GROUP = 'Grupo de marcadores',
  BIKE_STATION = 'Estación de bicicletas',
}

export enum EventForInteracctionsTypes {
  PLACE = 'Lugar',
  STOP = 'Parada',
  POI = 'Punto_de_interes',
}

interface EventForInteracctions {
  obj: any;
  type: EventForInteracctionsTypes;
  fromCluster?: boolean;
}

type TransportMode = 'WALK' | 'BUS' | 'SUBWAY' | 'RAIL' | 'TRAM' | 'FUNICULAR';
type TranslationMap = {
  [key in TransportMode]: string;
};

export interface SendEventOnChangePlanElementsType {
  obj: any;
  inputSelected: any;
}

export enum SendEventOnNewsAndAlertsClickCategoryType {
  NEWS = 'Noticias',
  LINE_ALERT = 'Aviso línea',
  STOP_ALERT = 'Aviso parada',
}

export interface SendEventOnNewsAndAlertsClickType {
  category: SendEventOnNewsAndAlertsClickCategoryType;
  name: string;
  detail?: string;
  operator?: string;
}

export interface SendEventOnOperatorsLinkType {
  operator: string;
  url: string;
}

export enum SendEventOnUserAccountActionsValues {
  LOGIN = 'iniciar_sesion',
  LOGOUT = 'cerrar_sesion',
  CREATE_ACCOUNT = 'crear_cuenta',
}

export enum SendEventOnUserAccountActionsLabels {
  LOGIN = 'Login realizado',
  LOGOUT = 'Cierre de sesión',
  CREATE_ACCOUNT = 'Registro completado',
}

export interface SendEventOnUserAccountActionsType {
  action: SendEventOnUserAccountActionsValues;
  label: SendEventOnUserAccountActionsLabels;
}

export const useGoogleAnalytics = () => {
  const [cookies] = useCookies();
  const hasConsent = cookies.cookieConsent;
  const cookiesInstance = useMemo(() => new Cookies(), []);
  const [getStationById] = useGetStationByIdMutation(); // Renombrado para evitar confusión
  const agencyState = useSelector(agencyInformation);
  const [searchNora] = useSearchNoraMutation();

  const initializeAnalytics = useCallback(() => {
      if (hasConsent) {
          const analyticsId = GeneralUtils.getAnalyticsEnvironment();
          console.log('analyticsId', analyticsId);
          
          if (analyticsId) {
              ReactGA.initialize(analyticsId);
              // console.log('Google Analytics inicializado con consentimiento');
          } else {
              console.warn('No se pudo obtener el ID de Analytics');
          }
      } else {
          // console.log('Google Analytics no inicializado - sin consentimiento');
      }
  }, [hasConsent]);


  const clearGACookies = useCallback(() => {
    const allCookies = cookiesInstance.getAll();
    Object.keys(allCookies).forEach(cookieName => {
      if (cookieName.startsWith('_ga')) {
        cookiesInstance.remove(cookieName, { path: '/' });
      }
    });
  }, [cookiesInstance]);

  // Función para generar un ID único para eventos
  const generateUUID = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(
      /[xy]/g,
      function (c) {
        const r = Math.floor(Math.random() * 16);
        const v = c === 'x' ? r : 8 + (r % 4);
        return v.toString(16);
      },
    );
  };

  const sendEvent = useCallback((eventParams: AnalyticsEvent) => {
    console.log('Enviando evento:', eventParams);
    if (hasConsent) {
      const event_date = new Date().toISOString();
      const event_id = generateUUID();
      const { action, ...params } = eventParams;
      ReactGA.event(action, {
        ...params,
        event_date,
        event_id,
      });
    }
  }, [hasConsent]);

  // Función auxiliar para traducir modos de transporte
  const translateTransportMode = useCallback((mode: TransportMode): string => {
    const translations: TranslationMap = {
      'WALK': 'A pie',
      'BUS': 'Autobús',
      'SUBWAY': 'Metro',
      'RAIL': 'Tren',
      'TRAM': 'Tranvía',
      'FUNICULAR': 'Funicular'
    };
    return translations[mode];
  }, []);

  // Función auxiliar para obtener el tipo de transporte de una ruta
  const getRouteTransportType = useCallback((plan: any): string => {
    // Obtener modos de transporte únicos
    const transportModes = Array.from(new Set(plan.lineSynoptic.map((line: any) => line.mode)));
    // Traducir cada modo al castellano y unirlos con coma
    return transportModes.map((mode): string => translateTransportMode(mode as TransportMode)).join(', ');
  }, [translateTransportMode]);

  // Función auxiliar para obtener el nombre del operador por ID
  const getOperatorNameById = useCallback(async (id: number): Promise<string> => {
    for (const operator of (agencyState.agenciesFilters as any[])) {
      if (!operator?.agencies || !Array.isArray(operator.agencies)) {
        continue;
      }
      const agency = operator.agencies.find((agency: any) => agency.id === id);
      if (agency) {
        return agency.shortName || '';
      }
    }
    return '';
  }, [agencyState.agenciesFilters]);

  const getDirectionUsingNora = useCallback(async (lat: string, long: string): Promise<any> => {
    let result = null;
    let searchText = lat + ',' + long;
    await searchNora({ locale: 'es', txt: searchText }).then((data: any) => {
      result = JSON.parse(JSON.stringify(data.data[0]));
      if (
        result.via &&
        result.via !== 'null' &&
        result.street &&
        result.street !== 'null'
      ) {
        result.name = `${result.via} ${result.street}`;
      } else {
        result.name = result.municipality;
      }
      if (
        result.portal !== 0 &&
        result.street &&
        result.street !== 'null'
      ) {
        result.direction = `${result.via} ${result.street} ${result.portal}, ${result.locality ?? result.municipality
          }`;
      } else {
        result.direction =
          result.locality ?? result.municipality;
      }

      result.typeId = 1;
    }).catch(() => {
      return null;
    });
    return result;
  }, []);

  // Función auxiliar para obtener información del marcador
  const getMarkerInfo = useCallback(async ({ marker }: any): Promise<MapMarkerResult | null> => {
    let result: MapMarkerResult = {
      label: null,
      type: null,
    };
    try {
      if (marker?.double) {
        const direction = await getDirectionUsingNora(marker.latitude, marker.longitude);
        result.type = MapMarkerResultLabel.DIRECTION;
        result.label = direction?.direction;
      } else if (marker?.direction) {
        // Caso: Dirección
        result.type = MapMarkerResultLabel.DIRECTION;
        result.label = marker.alias
          ? `${marker.alias.match(/:\s*(.*)$/)?.[1] || marker.alias}, ${marker.direction}`
          : marker.direction;
      } else if (marker?.categoryId) {
        // Caso: Punto de interés
        // const poi = await (Apis as any).categories.getCategory('es', marker.categoryId);
        result.type = MapMarkerResultLabel.POI;
        result.label = `${marker.name}${marker.address ? ` - ${marker.address}` : ''}`;
      }
      else if (marker?.agencyOriginId) {
        if (!marker?.stopMunicipality) {
          // Caso: Estación de bicicletas
          const response = await getStationById({ id: marker.id, locale: 'es' });
          if ('data' in response && response.data) {
            const station = response.data;
            result.type = MapMarkerResultLabel.BIKE_STATION;
            result.label = station.name;
          }
        }
        else {
          // Caso: Parada
          const stop = await Apis.stops.getStop('es', marker.id);
          result.type = MapMarkerResultLabel.STOP;
          result.label = `${stop.name}${stop.stopMunicipality ? ` (${stop.stopMunicipality})` : ''}`;
        }
      }
      else if (marker?.id && marker?.points) {
        // Caso: Grupo de paradas / Grupo de puntos de interés / Grupo de marcadores
        // Verificar si son paradas o puntos de interés
        const isStops = marker.points.some((point: any) => point.agencyOriginId);
        const isPOIs = marker.points.some((point: any) => point.poiOriginId);
        if (isStops && !isPOIs) {
          const labels = marker.points.map((option: any) => option.name);
          result.type = MapMarkerResultLabel.STOP_GROUP;
          result.label = labels.join(' | ');
        } else if (isPOIs && !isStops) {
          const labels = marker.points.map(
            (option: any) => `${option.name}${option.address ? ` - ${option.address}` : ''}`,
          );
          result.type = MapMarkerResultLabel.POI_GROUP;
          result.label = labels.join(' | ');
        } else {
          const stops = marker.points.filter((point: any) => point.agencyOriginId);
          const pois = marker.points.filter((point: any) => point.poiOriginId);
          const stopLabels = stops.map((option: any) => `Parada: ${option.name}`);
          const poiLabels = pois.map(
            (option: any) => `${option.name}${option.address ? ` - ${option.address}` : ''}`,
          );
          const allLabels = [...stopLabels, ...poiLabels];
          result.type = MapMarkerResultLabel.MARKERS_GROUP;
          result.label = allLabels.join(' | ');
        }
      }
      if (result.type && result.label) {
        return result;
      } else {
        return null;
      }
    } catch (error) {
      console.error('Error al obtener información del marcador:', error);
      return null;
    }
  }, [getStationById]);

  // Evento para click en menú
  const sendEventForMenuClick = useCallback(({ label, shouldSend }: sendEventForMenuClick) => {
    if (!shouldSend) return;
    sendEvent({
      event_category: 'Navegación',
      action: 'cambio_de_menu',
      event_label: label,
      platform_type: 'web',
    });
  }, [sendEvent]);

  // Evento para click en favoritos
  const sendEventForFavorites = useCallback(({ type, label, shouldSend }: sendEventForFavorites) => {
    if (!shouldSend) return;
    sendEvent({
      event_category: 'Favoritos',
      action: 'guardar_en_favoritos',
      event_type: type,
      event_label: label,
      platform_type: 'web',
    });
  }, [sendEvent]);

  // Evento para click en mapa
  const sendEventsOnMarkerClick = useCallback(async ({ marker }: any) => {
    const markerInfo = await getMarkerInfo({ marker });
    if (markerInfo) {
      sendEvent({
        action: 'interaccion_en_mapa',
        platform_type: 'web',
        event_label: markerInfo.label!,
        event_type: markerInfo.type!,
        event_category: 'Mapa',
      });
    }
  }, [getMarkerInfo, sendEvent]);

  // Evento para interacciones
  const sendEventForInteracctions = useCallback(({ obj, type, fromCluster }: EventForInteracctions) => {
    let event_label = null;
    if (fromCluster) {
      event_label = type === EventForInteracctionsTypes.POI ? `${obj.name}, ${obj.address})` : `${obj.name} (${obj.stopMunicipality})`
    } else {
      event_label = type === EventForInteracctionsTypes.PLACE ? obj.streetName : obj.name ? obj.name : obj.headSign
    }
    if(event_label) {
      sendEvent({
        event_category: 'Interacción',
        action: `clic_en_${type.toLowerCase()}`,
        platform_type: 'web',
        event_label: event_label
      });
    }
  }, [sendEvent]);

  // Evento para click en linea
  const sendEventsOnLineSelected = useCallback(async ({ obj }: any) => {
    console.log('sendEventsOnLineSelected', obj);
    try {
      const lineDetails = typeof obj === 'object'
        ? obj.codeStop ?  await (Apis as any).lines.get(obj.lineId) : obj
        : await (Apis as any).lines.get(obj);

      if (!lineDetails) return;
      const operatorName = await getOperatorNameById(lineDetails.agencyOriginId);
      if (operatorName) {
        sendEvent({
          event_category: 'Transporte',
          action: 'seleccion_de_linea',
          event_label: lineDetails.name,
          event_value: lineDetails.code,
          event_operator: operatorName,
        });
      }
    } catch (error) {
      console.error('Error al enviar evento de selección de línea:', error);
    }
  }, [getOperatorNameById, sendEvent]);

  // Evento para click en ruta
  const sendEventOnRouteSelected = useCallback(({ obj }: any) => {
    const transportType = getRouteTransportType(obj);
    sendEvent({
      event_category: 'Navegación',
      action: 'seleccion_de_transporte',
      event_label: transportType,
      event_duration: obj.duration,
      event_time_range: obj.startTime + '-' + obj.endTime,
      platform_type: 'web',
    });
  }, [getRouteTransportType, sendEvent]);

  // Evento para cambio de elementos en plan
  const sendEventOnChangePlanElements = useCallback(({ obj, inputSelected }: SendEventOnChangePlanElementsType) => {
    let action = null;
    const name = obj.category ? obj.streetName : obj.name;

    if (inputSelected.getId() === 'destination') {
      action = 'seleccionar_destino';
    } else if (inputSelected.getId() === 'origin') {
      action = 'seleccionar_origen';
    } else {
      action = 'añadir_parada';
    }

    sendEvent({
      event_category: 'Navegación',
      action: action,
      event_label: name,
      platform_type: 'web',
    });
  }, [sendEvent]);

  // Evento para click en noticia o alerta
  const sendEventOnNewsAndAlertsClick = useCallback(({ category, name, operator, detail }: SendEventOnNewsAndAlertsClickType) => {
    sendEvent({
      event_category: category,
      action: 'pulsar_noticia_aviso',
      event_label: name,
      platform_type: 'web',
      ...(detail !== undefined && { event_value: detail ?? 'Sin detalle' }),
      ...(operator !== undefined && { event_operator: operator }),
    });
  }, [sendEvent]);

  // Evento para click en enlace de operador
  const sendEventOnOperatorsLinkClick = useCallback(({ operator, url }: SendEventOnOperatorsLinkType) => {
    sendEvent({
      event_category: 'Enlaces',
      action: 'clic_en_enlace',
      event_label: operator,
      event_value: url,
      platform_type: 'web',
    });
  }, [sendEvent]);

  // Evento acciones sobre la cuenta (login/logout/crear cuenta/)
  const sendEventOnUserAccountActions = useCallback(({ action, label }: SendEventOnUserAccountActionsType) => {
    sendEvent({
      event_category: 'Cuenta',
      action,
      event_label: label,
      platform_type: 'web',
    });
  }, [sendEvent]);

  return {
    sendEvent,
    initializeAnalytics,
    clearGACookies,
    sendEventForMenuClick,
    sendEventForFavorites,
    sendEventsOnMarkerClick,
    sendEventForInteracctions,
    sendEventsOnLineSelected,
    sendEventOnRouteSelected,
    sendEventOnChangePlanElements,
    sendEventOnNewsAndAlertsClick,
    sendEventOnOperatorsLinkClick,
    sendEventOnUserAccountActions
  };
};
